import { GetQueryVariables } from "api/apiFunctions";
import { deleteRequest } from "api/commonActions/delete";
import { getRequest } from "api/commonActions/get";
import { patchRequest } from "api/commonActions/update";
import { AxiosResponse } from "axios";
import { Requests } from "config/api";
import {
  DeleteCarProps,
  DeleteCarReturnType,
  GetCarByTagProps,
  GetCarByTagReturnType,
  GetCarProps,
  GetCarRequestsProps,
  GetCarRequestsReturnType,
  GetCarReturnType,
  GetCarsProps,
  GetCarsReturnType,
  GetLiveCarsProps,
  GetLiveCarsReturnType,
  GetUserCarsProps,
  GetUserCarsReturnType,
  RepostCarProps,
  RepostCarReturnType,
  UpdateCarProps,
  UpdateCarReturnType,
  GetCarInspectionProps,
  GetCarInspectionReturnType,
  UpdateCarInspectionProps,
  UpdateCarInspectionReturnType,
} from "declarations/carServices";
import { QueryParamsProps } from "declarations/commonServices";

export const GetCars = async ({
  limit,
  offset,
  setLoading,
  fuel,
  listingType,
  make,
  maxMileage,
  maxPrice,
  maxYear,
  minMileage,
  minPrice,
  minYear,
  populate,
  search,
  sort,
  status,
  transmission,
  usageType,
  assigned,
  buyerName,
  buyerPhone,
}: GetCarsProps): Promise<GetCarsReturnType> => {
  let queryParams: QueryParamsProps = GetQueryVariables({
    limit,
    offset,
    fuel,
    listingType,
    make,
    maxMileage,
    maxPrice,
    maxYear,
    minMileage,
    minPrice,
    minYear,
    populate,
    search,
    sort,
    status,
    transmission,
    usageType,
    assigned,
    buyerName,
    buyerPhone,
  });

  try {
    setLoading && setLoading(true);

    const response: AxiosResponse = await getRequest({
      route: Requests.get.getCars,
      queryParams,
    });
    setLoading && setLoading(false);

    const status = response?.status;
    const cars = response.data?.cars;
    const count = response.data?.count;
    const message = response?.data?.msg;

    return {
      status: status,
      message: message,
      cars,
      count,
    };
  } catch (error) {
    setLoading && setLoading(false);
    throw error;
  }
};

export const GetLiveCars = async ({
  limit,
  offset,
  setLoading,
  search,
  statusChangedBy,
}: GetLiveCarsProps): Promise<GetLiveCarsReturnType> => {
  let queryParams: QueryParamsProps = GetQueryVariables({
    limit,
    offset,
    search,
    statusChangedBy,
  });

  try {
    setLoading && setLoading(true);
    const response: AxiosResponse = await getRequest({
      route: Requests.get.getLiveCars,
      queryParams,
    });
    setLoading && setLoading(false);

    const status = response?.status;
    const cars = response.data?.cars;
    const count = response.data?.count;
    const message = response?.data?.msg;

    return {
      status: status,
      message: message,
      cars,
      count,
    };
  } catch (error) {
    setLoading && setLoading(false);
    throw error;
  }
};

export const GetCarRequests = async ({
  setLoading,
  ...props
}: GetCarRequestsProps): Promise<GetCarRequestsReturnType> => {
  let queryParams: QueryParamsProps = GetQueryVariables(props);

  try {
    setLoading && setLoading(true);
    const response: AxiosResponse = await getRequest({
      route: Requests.get.getCarRequests,
      queryParams,
    });
    setLoading && setLoading(false);

    const status = response?.status;
    const requests = response.data?.requests;
    const count = response.data?.count;
    const message = response?.data?.msg;

    return {
      status: status,
      message: message,
      requests,
      count,
    };
  } catch (error) {
    setLoading && setLoading(false);
    throw error;
  }
};

export const GetCar = async ({
  setLoading,
  id,
}: GetCarProps): Promise<GetCarReturnType> => {
  try {
    setLoading && setLoading(true);
    const response: AxiosResponse = await getRequest({
      route: Requests.get.getCar,
      pathVariables: {
        id,
      },
    });
    setLoading && setLoading(false);

    const status = response?.status;
    const car = response.data?.car;
    const message = response?.data?.msg;

    return {
      status: status,
      message: message,
      car,
    };
  } catch (error) {
    setLoading && setLoading(false);
    throw error;
  }
};

export const GetAdminCar = async ({
  setLoading,
  id,
}: GetCarProps): Promise<GetCarReturnType> => {
  try {
    setLoading?.(true);
    const response: AxiosResponse = await getRequest({
      route: Requests.get.getAdminCar,
      pathVariables: { id: String(id) },
    });
    return {
      status: response?.status,
      message: response?.data?.msg,
      car: response?.data?.car,
    };
  } finally {
    setLoading?.(false);
  }
};

export const GetCarByTag = async ({
  setLoading,
  tag,
}: GetCarByTagProps): Promise<GetCarByTagReturnType> => {
  try {
    setLoading && setLoading(true);
    const response: AxiosResponse = await getRequest({
      route: Requests.get.getCarByTag,
      pathVariables: {
        tag,
      },
    });
    setLoading && setLoading(false);

    const status = response?.status;
    const car = response.data?.car;
    const message = response?.data?.msg;

    return {
      status: status,
      message: message,
      car,
    };
  } catch (error) {
    setLoading && setLoading(false);
    throw error;
  }
};

export const UpdateCar = async ({
  setLoading,
  description,
  color,
  fuelType,
  batteryRangeKm,
  location,
  make,
  mileage,
  model,
  phone,
  ownerPhone,
  brokerCount,
  price,
  transmissionType,
  usageType,
  year,
  id,
  listingType,
  assigned,
  buyerName,
  buyerPhone,
  existingImages,
  status: carStatus,
  inspectionStatus,
  inspectionScore,
  isDeal,
  actualSalePrice,
  actualSoldAt,
  outcomeVerified,
  withDriver,
  images = [],
}: UpdateCarProps): Promise<UpdateCarReturnType> => {
  try {
    setLoading && setLoading(true);
    const formData = new FormData();
    formData.append("description", description);
    formData.append("make", make);
    formData.append("color", color || "");
    formData.append("fuelType", fuelType);
    formData.append("batteryRangeKm", batteryRangeKm || "");
    formData.append("location", location || "");
    formData.append("mileage", mileage);
    formData.append("model", model);
    formData.append("phone", phone);
    if (ownerPhone !== undefined) formData.append("ownerPhone", ownerPhone);
    if (brokerCount !== undefined) formData.append("brokerCount", brokerCount);
    formData.append("price", price);
    formData.append("transmissionType", transmissionType);
    formData.append("usageType", usageType);
    formData.append("year", year);
    formData.append("listingType", listingType);
    if (withDriver !== undefined) formData.append("withDriver", withDriver);
    if (assigned !== undefined) formData.append("assigned", String(assigned));
    if (buyerName !== undefined) formData.append("buyerName", buyerName);
    if (buyerPhone !== undefined) formData.append("buyerPhone", buyerPhone);
    if (carStatus) formData.append("status", carStatus);
    if (inspectionStatus) formData.append("inspectionStatus", inspectionStatus);
    if (inspectionScore !== undefined) formData.append("inspectionScore", inspectionScore);
    if (isDeal !== undefined) formData.append("isDeal", String(isDeal));
    if (actualSalePrice !== undefined) formData.append("actualSalePrice", actualSalePrice);
    if (actualSoldAt !== undefined) formData.append("actualSoldAt", actualSoldAt);
    if (outcomeVerified !== undefined) formData.append("outcomeVerified", String(outcomeVerified));
    images.forEach((image) => formData.append("images", image, image.name));
    existingImages.forEach((image, index) => {
      formData.append(`existingImages[${image.key ?? index}]`, image.image);
    });
    const response: AxiosResponse = await patchRequest({
      route: Requests.update.updateCar,
      payload: formData,
      pathVariables: {
        id,
      },
    });
    setLoading && setLoading(false);

    const status = response?.status;
    const car = response?.data?.car;
    const error = response?.data?.error;
    const message = response?.data?.msg;

    return {
      status: status,
      message: message,
      car,
      error,
    };
  } catch (error) {
    setLoading && setLoading(false);
    throw error;
  }
};

export const GetCarInspection = async ({
  id,
  setLoading,
}: GetCarInspectionProps): Promise<GetCarInspectionReturnType> => {
  try {
    setLoading?.(true);
    const response: AxiosResponse = await getRequest({
      route: Requests.get.getCarInspection,
      pathVariables: { id: String(id) },
    });
    return {
      status: response?.status,
      message: response?.data?.msg,
      error: response?.data?.error,
      inspection: response?.data?.inspection,
    };
  } catch (error: any) {
    return { status: 0, error: error?.message || "Inspection could not be loaded" };
  } finally {
    setLoading?.(false);
  }
};

export const UpdateCarInspection = async ({
  id,
  payload,
  setLoading,
}: UpdateCarInspectionProps): Promise<UpdateCarInspectionReturnType> => {
  try {
    setLoading?.(true);
    const response: AxiosResponse = await patchRequest({
      route: Requests.update.updateCarInspection,
      pathVariables: { id: String(id) },
      payload,
    });
    return {
      status: response?.status,
      message: response?.data?.msg,
      error: response?.data?.error || response?.data?.msg,
      inspection: response?.data?.inspection,
    };
  } catch (error: any) {
    return { status: 0, error: error?.message || "Inspection could not be saved" };
  } finally {
    setLoading?.(false);
  }
};

export const RepostCar = async ({
  setLoading,
  id,
}: RepostCarProps): Promise<RepostCarReturnType> => {
  try {
    setLoading && setLoading(true);
    const response: AxiosResponse = await patchRequest({
      route: Requests.update.repostCar,
      pathVariables: {
        id,
      },
    });
    setLoading && setLoading(false);

    const status = response?.status;
    const car = response.data?.car;
    const error = response.data?.error;
    const message = response.data?.msg;

    return {
      status: status,
      message: message,
      car,
      error,
    };
  } catch (error) {
    setLoading && setLoading(false);
    throw error;
  }
};

export const GetUserCars = async ({
  limit,
  offset,
  id,
  setLoading,
}: GetUserCarsProps): Promise<GetUserCarsReturnType> => {
  let queryParams: QueryParamsProps = GetQueryVariables({
    limit,
    offset,
  });

  try {
    setLoading && setLoading(true);
    const response: AxiosResponse = await getRequest({
      route: Requests.get.getUserCars,
      queryParams,
      pathVariables: {
        id,
      },
    });
    setLoading && setLoading(false);

    const status = response?.status;
    const cars = response.data?.cars;
    const count = response.data?.count;
    const message = response?.data?.msg;

    return {
      status: status,
      message: message,
      cars,
      count,
    };
  } catch (error) {
    setLoading && setLoading(false);
    throw error;
  }
};

export const DeleteCar = async ({
  setLoading,
  id,
}: DeleteCarProps): Promise<DeleteCarReturnType> => {
  try {
    setLoading && setLoading(true);
    const response: AxiosResponse = await deleteRequest({
      route: Requests.delete.deleteCar,
      pathVariables: {
        id,
      },
    });
    setLoading && setLoading(false);

    const status = response?.status;
    const car = response.data?.car;
    const message = response?.data?.msg;

    return {
      status: status,
      message: message,
      car,
    };
  } catch (error) {
    setLoading && setLoading(false);
    throw error;
  }
};
